import type { ReactNode } from 'react';
import {
  Checkbox,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Truncated,
} from '@customerio/pluma-components/react';
import type { Relationship } from '@customerio/hydra-ui-api';
import { RelationshipRow, type RelationshipRowModel } from './relationship-row';

/** Checkbox, person, created_at and view, around the attribute columns. */
const FIXED_COLUMN_COUNT = 4;

export type RelationshipsTableBodyProps = {
  rows: RelationshipRowModel[];
  personSingular: string;
  canSelect: boolean;
  isPageSelected: boolean;
  isSelectionIndeterminate: boolean;
  onSelectPage: (checked: boolean) => void;
  onSelectRow: (relationship: Relationship, index: number, checked: boolean) => void;
  onViewRelationship: (relationship: Relationship) => void;
  /** The saved attribute columns; none shows a single "Relationship attributes" count column. */
  columns: readonly string[];
  emptyState: ReactNode;
};

export function RelationshipsTableBody({
  rows,
  personSingular,
  canSelect,
  isPageSelected,
  isSelectionIndeterminate,
  onSelectPage,
  onSelectRow,
  onViewRelationship,
  columns,
  emptyState,
}: RelationshipsTableBodyProps) {
  return (
    <Table caption="List of relationships" layout="fixed">
      <TableThead>
        <TableTr>
          <TableTh py="0" style={{ width: '42px' }}>
            <Checkbox
              name="check_all_on_page"
              isDisabled={!canSelect}
              isChecked={isPageSelected}
              isIndeterminate={isSelectionIndeterminate}
              onCheckedChange={onSelectPage}
              ariaLabel="Select all relationships"
              data-test-select-all-relationships
            />
          </TableTh>
          <TableTh>{personSingular}</TableTh>
          {columns.length === 0 ? (
            <TableTh data-test-attributes-column>Relationship attributes</TableTh>
          ) : (
            columns.map((column) => (
              <TableTh key={column} data-test-column-attribute={column}>
                <Truncated tooltip={column}>{column}</Truncated>
              </TableTh>
            ))
          )}
          <TableTh textAlign="right">created_at</TableTh>
          <TableTh textAlign="right" style={{ width: '64px' }} />
        </TableTr>
      </TableThead>
      <TableTbody>
        {rows.length === 0 ? (
          <TableTr>
            <TableTd colSpan={FIXED_COLUMN_COUNT + Math.max(columns.length, 1)}>{emptyState}</TableTd>
          </TableTr>
        ) : (
          rows.map((row) => (
            <RelationshipRow
              key={row.key}
              row={row}
              canSelect={canSelect}
              columns={columns}
              onSelectedChange={(checked) => onSelectRow(row.relationship, row.index, checked)}
              onView={() => onViewRelationship(row.relationship)}
            />
          ))
        )}
      </TableTbody>
    </Table>
  );
}
